import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, TablePagination,
  Chip, IconButton, Grid, Menu, MenuItem, ListItemIcon, ListItemText,
  FormControl, InputLabel, Select,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cardManagementService } from '../../services/cardManagementService';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../accounting/shared/StatsCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SessionDetailModal from './components/SessionDetailModal';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

const formatDateGMT3 = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const gmt3 = new Date(d.getTime() + 3 * 60 * 60 * 1000);
  return gmt3.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const CardSessions: React.FC = () => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId || '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCardNumber = searchParams.get('cardNumber') || '';

  const [sessions, setSessions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState(initialCardNumber);
  const [searchDebounced, setSearchDebounced] = useState(initialCardNumber);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [activeCount, setActiveCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [totalTrappedBalance, setTotalTrappedBalance] = useState(0);

  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [anchorElRow, setAnchorElRow] = useState<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadSessions = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await cardManagementService.getAllSessions(companyId, {
        page: page + 1,
        pageSize,
        search: searchDebounced || undefined,
        status: statusFilter || undefined,
      });
      setSessions(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
      setTotalTrappedBalance(res.data?.totalTrappedBalance || 0);
    } catch {
      setSessions([]);
    }
    setLoading(false);
  }, [companyId, page, pageSize, searchDebounced, statusFilter]);

  const loadCounts = useCallback(async () => {
    if (!companyId) return;
    try {
      const [activeRes, closedRes] = await Promise.all([
        cardManagementService.getAllSessions(companyId, { page: 1, pageSize: 1, status: 'ACTIVE' }),
        cardManagementService.getAllSessions(companyId, { page: 1, pageSize: 1, status: 'CLOSED' }),
      ]);
      setActiveCount(activeRes.data?.totalCount || 0);
      setClosedCount(closedRes.data?.totalCount || 0);
    } catch {}
  }, [companyId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const columns = useMemo<DataTableColumn<any>[]>(() => [
    {
      id: 'rowNumber',
      label: cm('sessions.table.no', 'No'),
      width: '60px',
      render: (_row: any, index: number) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {page * pageSize + index + 1}
        </Box>
      ),
    },
    {
      id: 'cardNumber',
      label: cm('sessions.table.cardNo', 'Card No'),
      render: (row: any) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {row.cardNumber || '—'}
        </Typography>
      ),
    },
    {
      id: 'cardUID',
      label: cm('sessions.table.cardUid', 'Card UID'),
      render: (row: any) => <>{row.cardUID || '—'}</>,
    },
    {
      id: 'accountName',
      label: cm('sessions.table.owner', 'Card Owner'),
      // Sahip cari ya da personel olabilir; ownerName backend'de türe göre çözülür.
      render: (row: any) => <>{row.ownerName || row.accountName || '—'}</>,
    },
    {
      id: 'status',
      label: cm('sessions.table.session', 'Session'),
      render: (row: any) => {
        const isActive = row.status === 'ACTIVE';
        return (
          <Chip
            label={isActive ? cm('sessions.filters.open', 'Open') : cm('sessions.filters.closed', 'Closed')}
            size="small"
            color={isActive ? 'info' : 'default'}
            variant={isActive ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      id: 'startedAt',
      label: cm('sessions.table.start', 'Start'),
      render: (row: any) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDateGMT3(row.startedAt)}
        </Typography>
      ),
    },
    {
      id: 'endedAt',
      label: cm('sessions.table.end', 'End'),
      render: (row: any) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {row.endedAt ? formatDateGMT3(row.endedAt) : '—'}
        </Typography>
      ),
    },
    {
      id: 'totalBalance',
      label: cm('sessions.table.remainingBalance', 'Remaining Balance'),
      align: 'right',
      render: (row: any) => {
        const balance = row.totalBalance || 0;
        if (balance === 0) {
          return <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'right' }}>0,00 ₺</Typography>;
        }
        const isClosed = row.status === 'CLOSED';
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              textAlign: 'right',
              color: isClosed && balance > 0 ? 'warning.main' : 'success.main',
            }}
          >
            {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(balance)} ₺
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      label: cm('sessions.table.actions', 'Actions'),
      align: 'right',
      render: (row: any) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow(prev => ({ ...prev, [row.id]: e.currentTarget })); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => setAnchorElRow(prev => ({ ...prev, [row.id]: null }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => {
              setAnchorElRow(prev => ({ ...prev, [row.id]: null }));
              setSelectedSession(row);
              setDetailOpen(true);
            }}>
              <ListItemIcon><InfoIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={cm('common.details', 'Details')} />
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
  ], [page, pageSize, anchorElRow]);

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/kart-yonetimi/kartlar')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <HistoryIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {cm('sessions.title', 'Card Sessions')}
          </Typography>
          <GuideButton guideKey="cards.sessions" sx={{ ml: 1 }} />
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && sessions.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<HistoryIcon />}
            color={STATS_COLORS.blue}
            title={`${activeCount + closedCount}`}
            subtitle={cm('sessions.stats.totalSessions', 'Total Sessions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<LockOpenIcon />}
            color={STATS_COLORS.green}
            title={`${activeCount}`}
            subtitle={cm('sessions.stats.openSessions', 'Open Sessions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<LockIcon />}
            color={STATS_COLORS.red}
            title={`${closedCount}`}
            subtitle={cm('sessions.stats.closedSessions', 'Closed Sessions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AccountBalanceWalletIcon />}
            color={STATS_COLORS.orange}
            title={formatStatsCurrency(totalTrappedBalance)}
            subtitle={cm('sessions.stats.remainingInside', 'Total Remaining Inside')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={cm('sessions.filters.searchPlaceholder', 'Search Card No, UID or Account...')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{cm('sessions.filters.status', 'Status')}</InputLabel>
              <Select
                value={statusFilter}
                label={cm('sessions.filters.status', 'Status')}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">{cm('sessions.filters.all', 'All')}</MenuItem>
                <MenuItem value="ACTIVE">{cm('sessions.filters.open', 'Open')}</MenuItem>
                <MenuItem value="CLOSED">{cm('sessions.filters.closed', 'Closed')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => { loadSessions(); loadCounts(); }} title={cm('common.refresh', 'Refresh')}>
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Tablo */}
      <AccountingDataTable<any>
        columns={columns}
        rows={sessions}
        getRowId={(row) => row.id}
        loading={loading}
        pagination={false}
        emptyMessage={cm('common.noDataSessions', 'No session records yet.')}
        emptyIcon={<HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={cm('sessions.tableAriaLabel', 'card sessions table')}
      />
      <Paper sx={{ borderRadius: 0, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, mt: -0.5 }}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={cm('common.rowsPerPage', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) => isEnglish ? `${from}–${to} of ${count}` : `${count} kayıttan ${from}–${to} arası`}
        />
      </Paper>

      {/* Oturum Detay Modalı */}
      <SessionDetailModal
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedSession(null); }}
        session={selectedSession}
        companyId={companyId}
      />
    </>
  );
};

export default CardSessions;
